// Onboarding — 4 hero panels shown on first visit (before /login).
// Visual canon: apps/web/src/design-canon/project/src/screens-flow.jsx
//   → OnboardingScreen. Per-panel background swaps the screen color and uses
//   a polaroid-like hero card with two tape strips, the same kicker/title/copy
//   stack, dot indicators, and a full-width CTA in the inverted color.
//
// First-visit gate: localStorage 'stumble.onboarding-complete'. The entry
// route (routes/index.tsx) redirects anonymous, first-visit users here.
//
// Navigation: swipe left/right (touch), tap NEXT button, tap a dot to jump.
// Reduced-motion: instant slide (no transform/opacity transition).
//
// Per ADR-0010: 100dvh, env(safe-area-inset-*), --safe-bottom, touch targets ≥44pt.

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react'

export const ONBOARDING_KEY = 'stumble.onboarding-complete'

export const Route = createFileRoute('/onboarding')({
  component: OnboardingRoute,
})

type Panel = {
  /** Background color CSS var or hex. */
  bg: string
  /** Foreground (text + dot border + CTA-fill) color CSS var or hex. */
  fg: string
  /** Eyebrow / kicker line above the headline. */
  kicker: string
  /** Display headline (rendered in display font, lowercase, can wrap with \n). */
  title: string
  /** Body copy, 1–2 lines. */
  copy: string
  /** Hero image path under apps/web/public/. */
  img: string
  /** Object position for the hero image (CSS background-position). */
  pos: string
  /** CTA label for the panel. */
  cta: string
  /** aria-label override for the hero region. */
  heroAlt: string
}

const PANELS: ReadonlyArray<Panel> = [
  {
    bg: 'var(--pink)',
    fg: 'var(--paper)',
    kicker: '01 / tap to drink',
    title: 'one tap.\none drink.',
    copy: 'Track your night without thinking. Beer, wine, cocktail, shot — tap when you finish.',
    img: '/cards/cheers-banana.jpg',
    pos: '55% 28%',
    cta: 'Next',
    heroAlt: 'Friends cheering with drinks',
  },
  {
    bg: 'var(--lime)',
    fg: 'var(--ink)',
    kicker: '02 / proof & polaroids',
    title: 'snap. stamp.\nshare.',
    copy: 'Every drink can get a polaroid. We stamp it with the venue + time — no filters required (but we add the Huji vibe).',
    img: '/cards/cheers-cooler.jpg',
    pos: '50% 60%',
    cta: 'Next',
    heroAlt: 'A cooler full of cold drinks',
  },
  {
    bg: 'var(--orange)',
    fg: 'var(--paper)',
    kicker: '03 / who’s out',
    title: 'your people\non the map.',
    copy: 'See your crew’s stumbles live tonight. Tap any pin to peek their venue + drink count.',
    img: '/cards/social-card.jpg',
    pos: '50% 45%',
    cta: 'Next',
    heroAlt: 'Friends out together at night',
  },
  {
    bg: 'var(--teal, #46d6c9)',
    fg: 'var(--ink)',
    kicker: '04 / wrapped',
    title: 'save the night.',
    copy: 'End your stumble → instant scrapbook collage. Share it. Save it. Re-live it tomorrow.',
    img: '/cards/start-card.jpg',
    pos: '50% 50%',
    cta: 'Get started',
    heroAlt: 'Recap scrapbook with photos and stamps',
  },
] as const

const SWIPE_THRESHOLD_PX = 50
const SWIPE_MAX_OFF_AXIS_PX = 60

function OnboardingRoute() {
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [direction, setDirection] = useState<1 | -1>(1)
  const touchStart = useRef<{ x: number; y: number; t: number } | null>(null)

  const reducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }, [])

  function finish() {
    try {
      localStorage.setItem(ONBOARDING_KEY, '1')
    } catch {
      // ignore (privacy mode / disabled storage)
    }
    navigate({ to: '/login' })
  }

  function goTo(next: number) {
    if (next === step) return
    setDirection(next > step ? 1 : -1)
    setStep(next)
  }

  function handleNext() {
    if (step < PANELS.length - 1) goTo(step + 1)
    else finish()
  }

  function handlePrev() {
    if (step > 0) goTo(step - 1)
  }

  function onTouchStart(e: React.TouchEvent) {
    const t = e.touches[0]
    if (!t) return
    touchStart.current = { x: t.clientX, y: t.clientY, t: Date.now() }
  }

  function onTouchEnd(e: React.TouchEvent) {
    const start = touchStart.current
    touchStart.current = null
    if (!start) return
    const t = e.changedTouches[0]
    if (!t) return
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    if (Math.abs(dy) > SWIPE_MAX_OFF_AXIS_PX) return // probably a scroll, ignore
    if (dx <= -SWIPE_THRESHOLD_PX) handleNext()
    else if (dx >= SWIPE_THRESHOLD_PX) handlePrev()
  }

  // Keep the latest handlers reachable from the keydown listener without
  // re-binding the window listener on every step change.
  const handlersRef = useRef({ handleNext, handlePrev, finish })
  handlersRef.current = { handleNext, handlePrev, finish }

  // Keyboard navigation for desktop preview / a11y.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        handlersRef.current.handleNext()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        handlersRef.current.handlePrev()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handlersRef.current.finish()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const s = PANELS[step]
  if (!s) return null

  const slideTransition: CSSProperties = reducedMotion
    ? { transition: 'none' }
    : { transition: 'transform 280ms ease, opacity 240ms ease' }

  return (
    <div
      className="stumble-screen no-head"
      data-testid="onboarding-screen"
      data-step={step}
      style={{
        background: s.bg,
        color: s.fg,
        transition: reducedMotion ? 'none' : 'background 280ms ease, color 200ms ease',
        overflow: 'hidden',
      }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          height: '100%',
          minHeight: '100dvh',
          padding: '0 22px',
          paddingTop: 'calc(env(safe-area-inset-top) + 40px)',
          paddingBottom: 'max(22px, calc(var(--safe-bottom, env(safe-area-inset-bottom)) + 22px))',
          gap: 14,
        }}
      >
        {/* Header row — kicker on left, Skip on right */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div
            className="eyebrow"
            data-testid="onboarding-kicker"
            style={{ color: s.fg, opacity: 0.78 }}
          >
            {s.kicker}
          </div>
          <button
            type="button"
            data-testid="onboarding-skip"
            onClick={finish}
            className="chip"
            style={{
              background: 'transparent',
              color: s.fg,
              borderColor: s.fg,
              minHeight: 44,
              minWidth: 64,
            }}
          >
            Skip
          </button>
        </div>

        {/* Hero card + headline + body — animated as a single panel block.
            Keying on the panel kicker forces a remount on step change so the
            slide-in animation runs each time, without using the array index. */}
        <div
          key={s.kicker}
          style={{
            display: 'grid',
            gridTemplateRows: '1fr auto auto',
            gap: 14,
            alignContent: 'stretch',
            ...slideTransition,
            // Slide-in from the swipe direction.
            animation: reducedMotion
              ? undefined
              : `${direction === 1 ? 'onboarding-in-right' : 'onboarding-in-left'} 280ms ease`,
          }}
        >
          <div
            data-testid="onboarding-hero"
            role="img"
            aria-label={s.heroAlt}
            style={{
              position: 'relative',
              borderRadius: 22,
              border: '3px solid var(--line)',
              background: `url(${s.img}) ${s.pos} / cover, var(--bg-deep, #1c1830)`,
              boxShadow: '0 10px 0 var(--shadow-ink)',
              minHeight: 240,
              overflow: 'hidden',
            }}
          >
            <span
              className="tape"
              aria-hidden
              style={{ top: 12, left: 18, transform: 'rotate(-6deg)' }}
            />
            <span
              className="tape"
              aria-hidden
              style={{ bottom: 14, right: 14, transform: 'rotate(7deg)' }}
            />
          </div>
          <h1
            className="h-lg"
            data-testid="onboarding-title"
            style={{ color: s.fg, whiteSpace: 'pre-line', margin: 0 }}
          >
            {s.title}
          </h1>
          <p
            className="body"
            data-testid="onboarding-copy"
            style={{ color: s.fg, maxWidth: '28ch', opacity: 0.94, margin: 0 }}
          >
            {s.copy}
          </p>
        </div>

        {/* Footer — dot indicators + primary CTA */}
        <div style={{ display: 'grid', gap: 14 }}>
          <div
            role="tablist"
            aria-label="Onboarding step"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0,
            }}
          >
            {PANELS.map((p, i) => {
              const active = i === step
              return (
                <button
                  key={p.kicker}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-label={`Go to step ${i + 1} of ${PANELS.length}`}
                  data-testid={`onboarding-dot-${i}`}
                  onClick={() => goTo(i)}
                  // 44pt touch target wrapping a smaller visual dot.
                  style={{
                    width: 44,
                    height: 44,
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      display: 'inline-block',
                      width: active ? 28 : 10,
                      height: 10,
                      borderRadius: 999,
                      background: active ? s.fg : 'transparent',
                      border: `1.5px solid ${s.fg}`,
                      transition: reducedMotion
                        ? 'none'
                        : 'width 200ms ease, background 200ms ease',
                    }}
                  />
                </button>
              )
            })}
          </div>

          <button
            type="button"
            data-testid="onboarding-cta"
            onClick={handleNext}
            className="btn full"
            style={{
              background: s.fg,
              color: s.bg,
              borderColor: s.fg,
              boxShadow: '0 6px 0 var(--shadow-ink)',
            }}
          >
            {s.cta}
          </button>
        </div>
      </div>

      {/* Slide-in keyframes (scoped to this route). */}
      <style>{`
        @keyframes onboarding-in-right {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes onboarding-in-left {
          from { opacity: 0; transform: translateX(-24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="onboarding-screen"] * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  )
}
